import { useState, useEffect } from "react";
import { TextField } from "@mui/material";
import { NumberPatternField } from "../../../../lib/inputs";
import { RegistrationFieldLabels } from "../../../../config/RegistrationConfigs";
import useForm from "../../../../lib/common/AsyncFormBox/useForm";

// Nationality ISO codes for CZ/SK countries
const KNOWN_NATIONALITY_FORMATS = ["CZ", "SK"];

/** Helper text for birth number when Masters verification applies (B7 series only). */
export const BIRTH_NUMBER_MASTERS_HELPER_TEXT = "Pro ověření Masters statusu";

/**
 * Normalize birth number by removing spaces and slashes
 *
 * @param {string} value - Birth number value
 * @returns {string} Normalized birth number
 */
function normalizeBirthNumber(value) {
  if (!value) return "";
  return value.replace(/\s/g, "").replace(/\//g, "");
}

/**
 * BirthNumberInput - Input for birth number with conditional formatting
 *
 * @param {Object} props
 * @param {string} [props.value] - Controlled birth number value
 * @param {string} [props.defaultValue] - Default birth number value (uncontrolled mode)
 * @param {string} [props.nationality] - Current nationality (determines if pattern formatting is used)
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.helperText] - Helper text to display (e.g. Masters hint for B7 races)
 * @param {Function} [props.onChange] - Callback when birth number changes, receives normalized value
 * @param {boolean} [props.required] - Whether the field is required
 * @param {boolean} [props.disabled] - Whether the field is disabled
 */
export default function BirthNumberInput({
  value,
  defaultValue = "",
  nationality = "",
  error = null,
  helperText = "",
  onChange,
  required = true,
  disabled = false,
}) {
  const form = useForm();
  const isControlled = value !== undefined;
  const normalizedDefault = normalizeBirthNumber(defaultValue);
  const [birthNumber, setBirthNumber] = useState(normalizedDefault);

  // Sync with controlled value or defaultValue
  useEffect(() => {
    if (isControlled) {
      const normalized = normalizeBirthNumber(value || "");
      setBirthNumber(normalized);
      form.setValue("birthNumber", normalized);
    } else if (defaultValue) {
      const normalized = normalizeBirthNumber(defaultValue);
      setBirthNumber(normalized);
      form.setValue("birthNumber", normalized);
    }
  }, [value, defaultValue, isControlled, form]);

  function handleTextFieldChange(event) {
    const value = event.target.value;
    const normalized = normalizeBirthNumber(value);
    setBirthNumber(normalized);
    form.setValue("birthNumber", normalized);
    onChange && onChange(normalized);
  }

  function handlePatternFieldChange(values) {
    const value = values.value || "";
    setBirthNumber(value);
    form.setValue("birthNumber", value);
    onChange && onChange(value);
  }

  const usePatternFormat = KNOWN_NATIONALITY_FORMATS.includes(nationality);
  const displayHelperText = error || helperText;

  if (usePatternFormat) {
    return (
      <NumberPatternField
        fullWidth
        required={required}
        disabled={disabled}
        label={RegistrationFieldLabels.birthNumber}
        name="birthNumber"
        value={birthNumber}
        onValueChange={handlePatternFieldChange}
        format="######/####"
        size="small"
        error={!!error}
        helperText={displayHelperText}
      />
    );
  }

  return (
    <TextField
      fullWidth
      required={required}
      disabled={disabled}
      label={RegistrationFieldLabels.birthNumber}
      name="birthNumber"
      value={birthNumber}
      onChange={handleTextFieldChange}
      size="small"
      error={!!error}
      helperText={displayHelperText}
    />
  );
}

// Export helper function for external use
export { normalizeBirthNumber };
